import { restoreExecutedSearch, resetSearchHistorySession } from '../../search-history/search-history.actions';
import { Action } from '@ngrx/store';
import { EMPTY as noFavoriteRequest, finalize } from 'rxjs';
import { groupBy, mergeMap as mergeSearchModes } from 'rxjs';
import { ensureCarList } from '../actions/car.actions';
import { filter } from 'rxjs';
import { clearCarPolygon } from '../actions/car.actions';
import { buildSearchRequest, searchErrorKey } from '../../models/search-request';
import {
  setCarPolygon,
  resetCarSearch,
  retryCarSearch,
  setCarMapView,
} from '../actions/car.actions';
import { logout, clearAuthUser, clearSessionState } from '../actions/auth.actions';
import { EMPTY, takeUntil } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiService } from '../../services/api.service';
import { catchError, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import {
  addCar,
  addCarFailure,
  addCarReport,
  addCarReportFailure,
  addCarReportSuccess,
  addCarSuccess,
  changeCarPage,
  changeCarPageSize,
  changeCarSort,
  getCar,
  getCarFailure,
  getCarSuccess,
  getTopCars,
  getTopCarsFailure,
  getTopCarsSuccess,
  likeCar,
  likeCarFailure,
  likeCarSuccess,
  removeCar,
  removeCarFailure,
  removeCarSuccess,
  searchCarReports,
  searchCarReportsFailure,
  searchCarReportsSuccess,
  searchCars,
  searchCarsFailure,
  searchCarsSuccess,
  searchMapCars,
  searchMapCarsSuccess,
  searchMapCarsFailure,
  setCarSold,
  setCarSoldFailure,
  setCarSoldSuccess,
  updateCar,
  updateCarFailure,
  updateCarReport,
  updateCarReportFailure,
  updateCarReportSuccess,
  updateCarSuccess,
  upgradeCar,
} from '../actions/car.actions';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { clearUser, getUser } from '../actions/users.action';
import { Store } from '@ngrx/store';
import { selectCarState } from '../selectors/car.selectors';

@Injectable()
export class CarSearchEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store
  ) {}

  initializeListOnTab$ = createEffect(() => this.actions$.pipe(
    ofType(setCarMapView), filter(action => !action.isCarMapView),
    withLatestFrom(this.store.select(selectCarState)),
    filter(([,state]) => !state.carResults && !state.listLoading),
    map(([,state]) => searchCars({ query: (state.query ?? state.listDraft ?? {}) as any }))
  ));

  searchCars$ = createEffect(() => this.actions$.pipe(
    ofType(restoreExecutedSearch, searchCars, searchMapCars, setCarPolygon, retryCarSearch, ensureCarList, changeCarSort, changeCarPage, changeCarPageSize),
    filter(action => action.type !== restoreExecutedSearch.type || action.domain === 'CAR'),
    withLatestFrom(this.store.select(selectCarState)),
    filter(([action,state]) => action.type === ensureCarList.type ? !state.historyRestored && !state.isCarMapView && !state.carResults :
      !([changeCarSort.type, changeCarPage.type, changeCarPageSize.type] as string[]).includes(action.type) || !state.isCarMapView),
    map(([action,state]) => ({action, state, isMap: (action.type === restoreExecutedSearch.type && action.mode === 'map') || action.type === searchMapCars.type || action.type === setCarPolygon.type || (action.type === retryCarSearch.type && state.isCarMapView)})),
    // One switchMap per mode: supersede only requests belonging to the same result set.
    groupBy(request => request.isMap),
    mergeSearchModes(requests => requests.pipe(switchMap(({state,isMap}) =>
      (isMap
        ? this.api.searchMapCars(buildSearchRequest(state, true)).pipe(map((results): Action => searchMapCarsSuccess({carMapResults: results})))
        : this.api.searchCars(buildSearchRequest(state, false)).pipe(map((results): Action => searchCarsSuccess({carResults: results})))
      ).pipe(
        takeUntil(this.actions$.pipe(ofType(logout, clearAuthUser, clearSessionState, clearCarPolygon, resetCarSearch),
          withLatestFrom(this.store.select(selectCarState)),
          filter(([action,current]) => action.type === resetCarSearch.type ? current.isCarMapView === isMap : action.type !== clearCarPolygon.type || isMap))),
        takeUntil(this.actions$.pipe(ofType(resetSearchHistorySession, restoreExecutedSearch), filter(action => action.type === resetSearchHistorySession.type || action.domain === 'CAR'))),
        catchError(error => of(isMap ? searchMapCarsFailure({error: searchErrorKey(error)}) : searchCarsFailure({error: searchErrorKey(error)})))
      )
    )))
  ));

  searchCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchCarsFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  searchMapCarsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchMapCarsFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  getTopCars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTopCars),
      mergeMap(() =>
        this.api.getTopCars().pipe(
          map((results) => getTopCarsSuccess({ topCarResults: results })),
          catchError((error) => of(getTopCarsFailure({ error: error.message })))
        )
      )
    )
  );

  getTopCarsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getTopCarsFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  getCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCar),
      mergeMap(({ carId }) =>
        this.api.getCarById(carId).pipe(
          map((fetchedCar) => getCarSuccess({ car: fetchedCar })),
          catchError((error) => of(getCarFailure({ error: error.message })))
        )
      )
    )
  );

  getCarSuccess$ = createEffect(() => this.actions$.pipe(ofType(getCarSuccess)), {
    dispatch: false,
  });

  getCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getCarFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_GET_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  addCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCar),
      mergeMap(({ formData }) =>
        this.api.addNewCar(formData).pipe(
          map((savedCar) => addCarSuccess({ car: savedCar })),
          catchError((error) => of(addCarFailure({ error: error.message })))
        )
      )
    )
  );

  addCarSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addCarSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('CAR_ADD_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  addCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addCarFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_ADD_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  updateCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCar),
      mergeMap(({ carId, formData }) =>
        this.api.editCar(formData, carId).pipe(
          map((updatedCar) => updateCarSuccess({ car: updatedCar })),
          catchError((error) => of(updateCarFailure({ error })))
        )
      )
    )
  );

  upgradeCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(upgradeCar),
      mergeMap(({ carId }) =>
        this.api.upgradeCar(carId).pipe(
          map((updatedCar) => updateCarSuccess({ car: updatedCar })),
          catchError((error) => of(updateCarFailure({ error })))
        )
      )
    )
  );

  updateCarSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateCarSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('CAR_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  updateCarSuccessLoadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCarSuccess),
      map((action) => {
        clearUser();
        return getUser({ userId: action.car.user.id });
      })
    )
  );

  updateCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateCarFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_UPDATE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  private readonly pendingFavorites = new Set<string>();

  likeCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(likeCar),
      mergeMap(({ userId, carId, isLike }) => {
        const key = userId + ':' + carId;
        if (this.pendingFavorites.has(key)) return noFavoriteRequest;
        this.pendingFavorites.add(key);
        return this.api.likeCar(userId, carId, isLike).pipe(
          finalize(() => this.pendingFavorites.delete(key)),
          map((updatedCar) => likeCarSuccess({ car: updatedCar, userId })),
          catchError((error) => of(likeCarFailure({ error })))
        );
      })
    )
  );

  likeCarSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(likeCarSuccess),
        tap((action) => {
          this.toastr.success(
            action.car.liked
              ? this.translate.instant('CAR_LIKE_SUCCESS')
              : this.translate.instant('CAR_UNLIKE_SUCCESS')
          );
        })
      ),
    { dispatch: false }
  );

  likeCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(likeCarFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_LIKE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  setCarSold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCarSold),
      mergeMap(({ carId }) =>
        this.api.setCarSold(carId).pipe(
          map((updatedCar) => setCarSoldSuccess({ car: updatedCar })),
          catchError((error) => of(setCarSoldFailure({ error })))
        )
      )
    )
  );

  setCarSoldSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCarSoldSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('CAR_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  setCarSoldFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCarSoldFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_UPDATE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  removeCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeCar),
      mergeMap(({ id }) =>
        this.api.deleteCar(id).pipe(
          map(() => removeCarSuccess({ id })),
          catchError((error) => of(removeCarFailure({ error: error.message })))
        )
      )
    )
  );

  removeCarSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(removeCarSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('CAR_DELETE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  removeCarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(removeCarFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_DELETE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  searchCarReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchCarReports),
      mergeMap(({ reportQuery }) =>
        this.api.searchCarReports(reportQuery).pipe(
          map((results) => searchCarReportsSuccess({ carReports: results })),
          catchError((error) => of(searchCarReportsFailure({ error: error.message })))
        )
      )
    )
  );

  searchCarReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchCarReportsFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('CAR_REPORT_SEARCH_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  addCarReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCarReport),
      mergeMap(({ entity, id, model }) =>
        this.api.createReport(entity, id, model).pipe(
          map((savedReport) => addCarReportSuccess({ report: savedReport })),
          catchError((error) => of(addCarReportFailure({ error: error.message })))
        )
      )
    )
  );

  addCarReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addCarReportSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('REPORT_ADD_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  addCarReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addCarReportFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('REPORT_ADD_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  updateCarReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCarReport),
      mergeMap(({ reportId, status }) =>
        this.api.updateCarReport(reportId, status).pipe(
          map((updatedCarReport) => updateCarReportSuccess({ report: updatedCarReport })),
          catchError((error) => of(updateCarReportFailure({ error })))
        )
      )
    )
  );

  updateCarReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateCarReportSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('REPORT_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );
}
